import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(b?.message || 'Request failed')
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return new Error(e?.response?.data?.message || e?.message || fallback)
}

export type HarmonizedTxItem = {
  kind: 'ledger' | 'withdrawal' | 'pos_sale'
  createdAt: string
  currency: string
  amount: string
  payload: Record<string, unknown>
}

export type ListTransactionsResult = {
  items: HarmonizedTxItem[]
  total: number
  page: number
  limit: number
}

export type AdminOrderRow = {
  id: number
  orderReference: string
  storeId: number
  status: string
  paymentStatus: string
  paymentMethod?: string
  currency: string
  total: string
  createdAt: string
  customer?: { email?: string; firstName?: string; lastName?: string }
}

export type ListOrdersResult = {
  items: AdminOrderRow[]
  total: number
  page: number
  limit: number
}

export type AdminTransactionDetail = {
  kind: 'ledger' | 'withdrawal' | 'pos_sale'
  detailRef: string
  id: number
  storeId: number
  currency: string
  createdAt: string
  salesChannel?: string
  amount?: string
  entryType?: string
  provider?: string | null
  providerReference?: string | null
  orderId?: number | null
  orderReference?: string | null
  status?: string
  payoutSnapshot?: unknown
  saleReference?: string
  subtotal?: string
  taxTotal?: string
  total?: string
  paymentMethod?: string
  staffUserId?: number | null
  staff?: { id: number; firstName?: string; lastName?: string; email?: string } | null
  lines?: Array<{
    id: number
    quantity: number
    unitSalePrice: string
    lineTotal: string
    productName?: string
    productSlug?: string
  }>
  store?: { id: number; name?: string; subdomain?: string } | null
}

export async function listAdminStoreTransactions(
  storeId: number,
  params?: {
    page?: number
    limit?: number
    channel?: 'all' | 'storefront' | 'in_store'
  },
): Promise<ListTransactionsResult> {
  try {
    const { data } = await api.get(`/admin/stores/${storeId}/transactions`, { params })
    return assertSuccess(data) as ListTransactionsResult
  } catch (e) {
    throw apiError(e, 'Failed to load transactions')
  }
}

export async function getAdminStoreTransaction(
  storeId: number,
  transactionRef: string,
): Promise<AdminTransactionDetail> {
  try {
    const encoded = encodeURIComponent(transactionRef.trim())
    const { data } = await api.get(`/admin/stores/${storeId}/transactions/${encoded}`)
    return assertSuccess(data) as AdminTransactionDetail
  } catch (e) {
    throw apiError(e, 'Failed to load transaction')
  }
}

export async function listAdminStoreOrders(
  storeId: number,
  params?: {
    page?: number
    limit?: number
    status?: string
    paymentStatus?: string
  },
): Promise<ListOrdersResult> {
  try {
    const { data } = await api.get(`/admin/stores/${storeId}/orders`, { params })
    return assertSuccess(data) as ListOrdersResult
  } catch (e) {
    throw apiError(e, 'Failed to load orders')
  }
}
